"use client";

// ── The submissions tab (`EX-PUB16`) ─────────────────────────────────────────
// `EX-PUB14` gave the contest page a submissions tab holding the ten-row strip
// that used to sit under the problem list. This is the surface that tab was
// always meant to be: a filter, real pages, a row that opens to its detail, and
// once the round is over the whole field's list beside your own.
//
// **Two lists, two routes, one panel.** Your own comes from the route that has
// no parameter for whose submissions these are; the field's comes from a second
// route the server refuses until the scoreboard is open (ADR-0064 decision 9).
// The switch between them is rendered off `contest.submissionsRevealed`, which
// is the server's own predicate rather than "ended and not frozen" re-derived
// here, so a tab that offers the list and a route that refuses it cannot
// disagree.
//
// **No source code on either list.** A program opens on its own read, and only
// once the organizer has published the round's submissions (`EX-SRC01`,
// ADR-0082 decision 3); the field list's `sourceOpen` says whether they have,
// and a signed-in reader then gets a Code button per row. `EX-SCB09`: your own
// rows always carry one, which opens your program in the result sheet
// (`OwnSubmissionSheet`) on the contest's own reads.
//
// **`EX-HCK01`: a third list, Your hacks**, for anybody signed in once the
// round's board is final; an accepted program opened from the field list
// offers "Try to break it" when its problem takes uphacks (`uphackLetters`).

import { useMemo, useState } from "react";
import { useTranslations } from "next-intl";
import { ChevronDown, ChevronLeft, ChevronRight, Code2, Wrench } from "lucide-react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ProgramSourceView } from "@/components/code/ProgramSourceView";
import { VerdictChip, SubmissionResultDetail } from "@/features/judge/components/workspace/SubmissionResult";
import { JUDGE_VERDICTS, type JudgeVerdict } from "@/features/quiz/coding-task-api";
import { formatRuntimeLanguageName } from "@/features/quiz/lib/coding-task-format";
import { useContestFieldSubmissionSource, useContestFieldSubmissions, useMyContestSubmissions } from "../hooks";
import { OwnSubmissionSheet } from "./OwnSubmissionSheet";
import { MyUphacksPanel } from "./uphack/MyUphacksPanel";
import { UphackSheet } from "./uphack/UphackSheet";
import { formatDateTime } from "../lib/format";
import { useContestDisplayZone } from "../lib/display-zone";
import type { ContestSubmissionListQuery, PublicContestDetail } from "../types";

type Scope = "mine" | "field" | "hacks";

/** `answer` is the letter's (`EX-FNC06`): a function problem's hack is typed by parameter. */
type HackTarget = { submissionId: string; letter: string; name: string | null; answer?: "function" };

function Select({
  label,
  value,
  onChange,
  children,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  children: React.ReactNode;
}) {
  return (
    <label className="flex items-center gap-1.5 text-xs font-semibold text-[rgb(var(--muted-foreground))]">
      <span className="sr-only">{label}</span>
      <select
        aria-label={label}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="h-11 rounded-lg border border-[rgb(var(--border))] bg-white px-2.5 text-sm font-medium text-neutral-800"
      >
        {children}
      </select>
    </label>
  );
}

/** The language a submission was written in, as one short cell. */
function LanguageCell({ languageKey, languageVersion }: { languageKey?: string; languageVersion?: string }) {
  if (!languageKey) return <span className="text-xs text-neutral-300">-</span>;
  return (
    <span className="font-mono text-[11px] text-[rgb(var(--muted-foreground))]">
      {formatRuntimeLanguageName(languageKey)}
      {languageVersion ? <span className="text-neutral-400">{` ${languageVersion}`}</span> : null}
    </span>
  );
}

/** `EX-SRC01`: one published program under the field list. */
function FieldProgram({
  slug,
  submissionId,
  uphackLetters,
  onHack,
}: {
  slug: string;
  submissionId: string;
  /** `EX-HCK01`: the letters whose problem takes hacks; an accepted program of one offers the sheet. */
  uphackLetters: readonly string[];
  onHack: (target: HackTarget) => void;
}) {
  const t = useTranslations("contest.page");
  const tHack = useTranslations("contest.uphack");
  const query = useContestFieldSubmissionSource(slug, submissionId);
  if (query.isPending) return <Skeleton className="h-40 w-full rounded-xl" />;
  if (query.isError) return <p className="text-sm text-red-700">{t("submissionsProgramFailed")}</p>;
  const program = query.data;
  return (
    <div className="space-y-2">
      <p className="text-sm text-neutral-700">
        {t("submissionsProgramOf", { name: program.name ?? "-", letter: program.letter })}
      </p>
      <ProgramSourceView
        source={program.sourceCode}
        languageKey={program.languageKey}
        languageName={formatRuntimeLanguageName(program.languageKey)}
        languageVersion={program.languageVersion}
        labels={{
          lines: t("programLines", { count: program.sourceCode.split("\n").length }),
          wrap: t("programWrap"),
          copy: t("programCopy"),
          copied: t("programCopied"),
        }}
      />
      {program.verdict === "accepted" && uphackLetters.includes(program.letter) ? (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-primary/20 bg-primary/5 px-4 py-3">
          <span className="text-sm text-neutral-700">{tHack("thinkWrong")}</span>
          <Button
            type="button"
            aria-haspopup="dialog"
            onClick={() => onHack({ submissionId: program.submissionId, letter: program.letter, name: program.name })}
          >
            <Wrench className="h-4 w-4" aria-hidden />
            {tHack("tryBreak")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

export function ContestSubmissionsPanel({
  contest,
  locale,
  initialFilter,
}: {
  contest: PublicContestDetail;
  locale: string;
  /** `EX-SCB05`: where a standings popup's "Open in Submissions" lands: a letter, own or the field's. */
  initialFilter?: { letter: string; scope: Scope };
}) {
  const t = useTranslations("contest.page");
  const zone = useContestDisplayZone();
  const tJudge = useTranslations("judge");
  const tHack = useTranslations("contest.uphack");

  const revealed = contest.submissionsRevealed === true;
  /** Somebody who never registered has no list of their own to read. */
  // `EX-VRT01`: a virtual taker reads their try's own rows.
  const virtualTry = Boolean(contest.viewer.virtual?.attempt);
  const canReadOwn = contest.viewer.isRegistered || virtualTry;
  const [scope, setScope] = useState<Scope>(initialFilter?.scope ?? (canReadOwn ? "mine" : "field"));
  const [letter, setLetter] = useState(initialFilter?.letter ?? "");
  const [verdict, setVerdict] = useState("");
  const [page, setPage] = useState(1);
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [openProgram, setOpenProgram] = useState<string | null>(null);
  /** `EX-SCB09`: one of your own rows, open in the result sheet. */
  const [ownSheet, setOwnSheet] = useState<{ submissionId: string; letter: string } | null>(null);
  /** `EX-HCK01`: the accepted program the reader is trying to break. */
  const [hackTarget, setHackTarget] = useState<HackTarget | null>(null);
  const isAuthenticated = contest.viewer.isAuthenticated;

  const query: ContestSubmissionListQuery = useMemo(
    () => ({
      ...(letter ? { letter } : {}),
      ...(verdict ? { verdict: verdict as JudgeVerdict } : {}),
      ...(page > 1 ? { page } : {}),
    }),
    [letter, verdict, page],
  );

  /**
   * Which list is actually being read, and therefore which query runs.
   *
   * The `revealed` clause is a second lock on the same door: the switch is not
   * rendered at all on a contest the server would refuse, so `scope` cannot
   * reach `"field"` there today, and a mutation test proves as much by staying
   * green when the clause is removed. It is kept because "the switch is hidden"
   * is a rendering detail and this line is the one that decides whether a
   * request is made at all.
   */
  const activeScope: Scope =
    scope === "hacks" && revealed && isAuthenticated
      ? "hacks"
      : !canReadOwn
        ? "field"
        : revealed && scope !== "hacks"
          ? scope
          : "mine";
  // `EX-HCK01`: the lists this reader has, in the switch's order.
  const scopes: Scope[] = revealed
    ? [...(canReadOwn ? (["mine"] as const) : []), "field", ...(isAuthenticated ? (["hacks"] as const) : [])]
    : [];
  const mine = useMyContestSubmissions(contest.slug, activeScope === "mine", query, virtualTry);
  const field = useContestFieldSubmissions(contest.slug, query, revealed && activeScope === "field");

  const letters = (contest.problems ?? []).map((problem) => problem.letter);
  const problemTitleOf = (value: string) => {
    const problem = (contest.problems ?? []).find((entry) => entry.letter === value);
    return problem ? (locale === "bn" && problem.title_bn ? problem.title_bn : problem.title_en) : null;
  };
  const changeFilter = (apply: () => void) => {
    apply();
    setPage(1);
    setOpenRow(null);
    setOpenProgram(null);
  };
  // `EX-SRC01`: the round's programs are open, and this reader may read them.
  const sourceOpen = activeScope === "field" && field.data?.sourceOpen === true;
  const canOpenProgram = sourceOpen && isAuthenticated;

  const active = activeScope === "mine" ? mine : field;
  const totalPages =
    activeScope === "mine" ? (mine.data?.totalPages ?? 1) : (field.data?.totalPages ?? 1);

  const filters = (
    <div className="flex flex-wrap items-center gap-2">
      {scopes.length > 1 ? (
        <div className="inline-flex rounded-lg border border-[rgb(var(--border))] bg-white p-1">
          {scopes.map((value) => (
            <Button
              variant="tertiary"
              key={value}
              type="button"
              aria-pressed={activeScope === value}
              onClick={() => changeFilter(() => setScope(value))}
              className={cn(
                "min-h-11 rounded-lg px-3 text-sm font-semibold transition-colors",
                activeScope === value ? "bg-neutral-900 text-white" : "text-neutral-600 hover:bg-neutral-50",
              )}
            >
              {value === "hacks"
                ? tHack("scopeHacks")
                : t(value === "mine" ? "submissionsScopeMine" : "submissionsScopeField")}
            </Button>
          ))}
        </div>
      ) : null}

      {letters.length > 0 && activeScope !== "hacks" ? (
        <Select
          label={t("submissionsFilterProblem")}
          value={letter}
          onChange={(value) => changeFilter(() => setLetter(value))}
        >
          <option value="">{t("submissionsAllProblems")}</option>
          {letters.map((value) => (
            <option key={value} value={value}>
              {value}
            </option>
          ))}
        </Select>
      ) : null}

      {activeScope !== "hacks" ? (
        <Select
          label={t("submissionsFilterVerdict")}
          value={verdict}
          onChange={(value) => changeFilter(() => setVerdict(value))}
        >
          <option value="">{t("submissionsAllVerdicts")}</option>
          {JUDGE_VERDICTS.map((value) => (
            <option key={value} value={value}>
              {tJudge(`verdict.${value}`)}
            </option>
          ))}
        </Select>
      ) : null}
    </div>
  );

  if (activeScope === "hacks") {
    return (
      <div className="space-y-3">
        {filters}
        <MyUphacksPanel slug={contest.slug} />
      </div>
    );
  }

  const pager =
    totalPages > 1 ? (
      <div className="flex items-center justify-center gap-3">
        <Button
          variant="tertiary"
          type="button"
          onClick={() => setPage((value) => Math.max(1, value - 1))}
          disabled={page <= 1}
          className="inline-flex h-11 items-center gap-1 rounded-lg border border-[rgb(var(--border))] bg-white px-2.5 text-xs font-medium text-neutral-700 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
          {t("submissionsPrevious")}
        </Button>
        <span className="text-xs text-[rgb(var(--muted-foreground))]">
          {t("submissionsPageOf", { page, pages: totalPages })}
        </span>
        <Button
          variant="tertiary"
          type="button"
          onClick={() => setPage((value) => Math.min(totalPages, value + 1))}
          disabled={page >= totalPages}
          className="inline-flex h-11 items-center gap-1 rounded-lg border border-[rgb(var(--border))] bg-white px-2.5 text-xs font-medium text-neutral-700 transition-colors hover:bg-neutral-50 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {t("submissionsNext")}
          <ChevronRight className="h-3.5 w-3.5" />
        </Button>
      </div>
    ) : null;

  if (active.isPending) {
    return (
      <div className="space-y-3">
        {filters}
        <Skeleton className="h-48 w-full rounded-xl" />
      </div>
    );
  }

  const empty = (
    <p className="rounded-xl border border-[rgb(var(--border))] bg-white p-8 text-center text-sm text-[rgb(var(--muted-foreground))]">
      {activeScope === "mine" ? t("noSubmissions") : t("noFieldSubmissions")}
    </p>
  );

  return (
    <div className="space-y-3">
      {filters}

      {activeScope === "mine" ? (
        (mine.data?.items ?? []).length === 0 ? (
          empty
        ) : (
          <div className="overflow-hidden rounded-xl border border-[rgb(var(--border))] bg-white">
            {(mine.data?.items ?? []).map((item) => {
              const open = openRow === item.submissionId;
              return (
                <div key={item.submissionId} className="border-b border-[rgb(var(--border))] last:border-b-0">
                  <div className="flex items-center transition-colors hover:bg-neutral-50">
                  <Button
                    type="button"
                    variant={null}
                    size={null}
                    aria-expanded={open}
                    onClick={() => setOpenRow(open ? null : item.submissionId)}
                    className="flex min-w-0 flex-1 items-center justify-start gap-3 whitespace-normal rounded-none py-3 pl-4 pr-2 text-left"
                  >
                    <span className="w-6 shrink-0 text-sm font-bold text-neutral-900">
                      {item.letter ?? "-"}
                    </span>
                    <VerdictChip status={item.status} verdict={item.verdict ?? null} />
                    <LanguageCell
                      languageKey={item.languageKey}
                      languageVersion={item.languageVersion}
                    />
                    <span className="ml-auto text-[11px] tabular-nums text-[rgb(var(--muted-foreground))]">
                      {formatDateTime(item.createdAt, locale, zone)}
                    </span>
                    <ChevronDown
                      className={cn(
                        "h-4 w-4 shrink-0 text-neutral-400 transition-transform",
                        open && "rotate-180",
                      )}
                      aria-hidden
                    />
                  </Button>
                  {item.letter ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      aria-haspopup="dialog"
                      aria-label={t("submissionsOpenOwnCode", { letter: item.letter })}
                      onClick={() => setOwnSheet({ submissionId: item.submissionId, letter: item.letter! })}
                      className="mr-3 shrink-0"
                    >
                      <Code2 className="h-3.5 w-3.5" aria-hidden />
                      {t("submissionsViewCode")}
                    </Button>
                  ) : null}
                  </div>
                  {open ? (
                    <div className="border-t border-[rgb(var(--border))] bg-neutral-50/60 px-4 py-3">
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-[rgb(var(--muted-foreground))]">
                        <span className="tabular-nums">
                          {tJudge("scoreValue", { score: item.score, maxScore: item.maxScore })}
                        </span>
                        {item.timeMs !== undefined ? (
                          <span className="tabular-nums">{`${item.timeMs} ms`}</span>
                        ) : null}
                        {item.memoryKb !== undefined ? (
                          <span className="tabular-nums">{`${item.memoryKb} KB`}</span>
                        ) : null}
                      </div>
                      <div className="mt-2">
                        <SubmissionResultDetail result={item} />
                      </div>
                      <p className="mt-2 text-[11px] text-[rgb(var(--muted-foreground))]">
                        {t("submissionsCountOnly")}
                      </p>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )
      ) : (field.data?.items ?? []).length === 0 ? (
        empty
      ) : (
        <>
        {sourceOpen ? (
          <p className="rounded-lg bg-neutral-50 px-3 py-2 text-sm text-neutral-600">
            {t(isAuthenticated ? "submissionsSourceOpen" : "submissionsSourceOpenSignIn")}
          </p>
        ) : null}
        {/*
          `relative` makes this box the containing block of the `sr-only`
          labels in the table (absolutely positioned). Without it they escape
          the sideways scroll and widen the whole page on a phone.
        */}
        <div
          data-testid="submissions-scroll"
          className="relative overflow-x-auto rounded-xl border border-[rgb(var(--border))] bg-white"
        >
          <table className="w-full min-w-[520px] border-collapse text-sm">
            <thead>
              <tr className="bg-neutral-50 text-xs font-semibold uppercase tracking-[0.06em] text-[rgb(var(--muted-foreground))]">
                <th className="px-4 py-3 text-left">{t("submissionsWho")}</th>
                <th className="px-3 py-3 text-center">{t("submissionsProblem")}</th>
                <th className="px-3 py-3 text-left">{t("submissionsLanguage")}</th>
                <th className="px-3 py-3 text-left">{t("submissionsVerdict")}</th>
                <th className="px-4 py-3 text-right">{t("submissionsWhen")}</th>
                {canOpenProgram ? (
                  <th className="px-3 py-3">
                    <span className="sr-only">{t("submissionsProgram")}</span>
                  </th>
                ) : null}
              </tr>
            </thead>
            <tbody>
              {(field.data?.items ?? []).map((item) => (
                <tr key={item.submissionId} className="border-t border-[rgb(var(--border))]">
                  <td className="px-4 py-3 font-medium text-neutral-900">{item.name ?? "-"}</td>
                  <td className="px-3 py-3 text-center font-bold text-neutral-900">
                    {item.letter ?? "-"}
                  </td>
                  <td className="px-3 py-3">
                    <LanguageCell
                      languageKey={item.languageKey}
                      languageVersion={item.languageVersion}
                    />
                  </td>
                  <td className="px-3 py-3">
                    <VerdictChip status={item.status} verdict={item.verdict ?? null} />
                  </td>
                  <td className="px-4 py-3 text-right text-[11px] tabular-nums text-[rgb(var(--muted-foreground))]">
                    {formatDateTime(item.createdAt, locale, zone)}
                  </td>
                  {canOpenProgram ? (
                    <td className="px-3 py-3 text-right">
                      <button
                        type="button"
                        aria-pressed={openProgram === item.submissionId}
                        onClick={() => setOpenProgram(item.submissionId)}
                        className="inline-flex h-9 items-center gap-1 rounded-lg px-2 text-xs font-semibold text-primary hover:bg-primary/10"
                      >
                        <Code2 className="h-3.5 w-3.5" aria-hidden />
                        {t("submissionsViewCode")}
                      </button>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {canOpenProgram && openProgram ? (
          <FieldProgram
            slug={contest.slug}
            submissionId={openProgram}
            uphackLetters={field.data?.uphackLetters ?? []}
            onHack={(next) => {
              const answer = (contest.problems ?? []).find((entry) => entry.letter === next.letter)?.answer;
              setHackTarget(answer === "function" ? { ...next, answer } : next);
            }}
          />
        ) : null}
        </>
      )}

      {pager}

      {hackTarget ? (
        <UphackSheet
          key={hackTarget.submissionId}
          open
          slug={contest.slug}
          target={hackTarget}
          onClose={() => setHackTarget(null)}
          onSent={() => {
            setHackTarget(null);
            changeFilter(() => setScope("hacks"));
          }}
        />
      ) : null}

      {ownSheet ? (
        <OwnSubmissionSheet
          key={ownSheet.submissionId}
          slug={contest.slug}
          letter={ownSheet.letter}
          problemTitle={problemTitleOf(ownSheet.letter)}
          contestTitle={locale === "bn" && contest.title_bn ? contest.title_bn : contest.title_en}
          contestStartAt={virtualTry ? (contest.viewer.virtual?.attempt?.startedAt ?? null) : (contest.startAt ?? null)}
          initialSubmissionId={ownSheet.submissionId}
          onClose={() => setOwnSheet(null)}
          virtual={virtualTry}
        />
      ) : null}
    </div>
  );
}
